import React, { useState, useEffect } from 'react';
import { leaderboardAPI } from '../../api/endpoints';
import { formatXPUNT } from '../../utils/helpers';
import { 
  FaTrophy, FaMedal, FaUser, FaChartLine,
  FaCrown, FaSearch
} from 'react-icons/fa';
import LoadingSpinner from '../common/LoadingSpinner';
import './Leaderboard.css';

const Leaderboard = () => {
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtered, setFiltered] = useState([]);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      setFiltered(
        leaderboard.filter(item =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    } else {
      setFiltered(leaderboard);
    }
  }, [searchTerm, leaderboard]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await leaderboardAPI.get({ limit: 100 });
      setLeaderboard(response.data || []);
      setFiltered(response.data || []);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <FaCrown className="rank-icon gold" />;
    if (rank === 2) return <FaMedal className="rank-icon silver" />;
    if (rank === 3) return <FaMedal className="rank-icon bronze" />;
    return <span className="rank-number">#{rank}</span>;
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return 'gold';
    if (rank === 2) return 'silver';
    if (rank === 3) return 'bronze';
    return '';
  };

  if (loading) {
    return (
      <div className="leaderboard-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="leaderboard-page">
      <div className="page-header">
        <div>
          <h1>
            <FaTrophy className="header-icon" /> Leaderboard
          </h1>
          <p className="page-subtitle">
            Top punters ranked by points and performance
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="search-section">
        <div className="search-input-wrapper">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Top 3 Podium */}
      {leaderboard.length >= 3 && (
        <div className="podium">
          <div className="podium-item second">
            <div className="podium-avatar">
              {leaderboard[1]?.name?.[0] || '?'}
            </div>
            <span className="podium-rank">🥈</span>
            <span className="podium-name">{leaderboard[1]?.name || 'Unknown'}</span>
            <span className="podium-points">{leaderboard[1]?.points || 0} pts</span>
            <div className="podium-bar second" style={{ height: '60%' }}></div>
          </div>
          <div className="podium-item first">
            <div className="podium-avatar">
              {leaderboard[0]?.name?.[0] || '?'}
            </div>
            <span className="podium-rank">👑</span>
            <span className="podium-name">{leaderboard[0]?.name || 'Unknown'}</span>
            <span className="podium-points">{leaderboard[0]?.points || 0} pts</span>
            <div className="podium-bar first" style={{ height: '100%' }}></div>
          </div>
          <div className="podium-item third">
            <div className="podium-avatar">
              {leaderboard[2]?.name?.[0] || '?'}
            </div>
            <span className="podium-rank">🥉</span>
            <span className="podium-name">{leaderboard[2]?.name || 'Unknown'}</span>
            <span className="podium-points">{leaderboard[2]?.points || 0} pts</span>
            <div className="podium-bar third" style={{ height: '40%' }}></div>
          </div>
        </div>
      )}

      {/* Full List */}
      <div className="leaderboard-list">
        <div className="list-header">
          <span className="col-rank">Rank</span>
          <span className="col-user">User</span>
          <span className="col-points">Points</span>
          <span className="col-wins">Wins</span>
          <span className="col-winrate">Win Rate</span>
          <span className="col-winnings">Winnings</span>
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state">
            <p>No users found</p>
          </div>
        ) : (
          filtered.map((item) => (
            <div 
              key={item.id} 
              className={`list-item ${getRankBadge(item.rank)}`}
            >
              <span className="col-rank">
                {getRankIcon(item.rank)}
              </span>
              <span className="col-user">
                <div className="user-info">
                  <div className="user-avatar-small">
                    {item.name?.[0] || '?'}
                  </div>
                  <span>{item.name}</span>
                </div>
              </span>
              <span className="col-points">{item.points}</span>
              <span className="col-wins">{item.wins || 0}</span>
              <span className="col-winrate">{item.win_rate || 0}%</span>
              <span className="col-winnings">{formatXPUNT(item.total_winnings || 0)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Leaderboard;